import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * Synthetic horizontal scrollbar synced to a scroll container.
 * Attach `stickyRef` to a track element and size an inner child with `scrollWidth`.
 *
 * Pass `bindKey` whenever the scroll/track nodes may mount or remount (e.g. after
 * loading finishes). Effects only see a stable ref object, so without `bindKey`
 * they would never rebind once they ran while the nodes were missing.
 */
export function useStickyScrollbar(scrollRef: RefObject<HTMLDivElement | null>, bindKey: unknown = true) {
  const stickyRef = useRef<HTMLDivElement>(null)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)
  const syncing = useRef(false)

  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl || !bindKey) {
      setScrollWidth(0)
      setHasHorizontalOverflow(false)
      return
    }

    const measure = () => {
      const nextWidth = scrollEl.scrollWidth
      setScrollWidth(nextWidth)
      // 1px slack avoids false positives from subpixel rounding.
      setHasHorizontalOverflow(nextWidth > scrollEl.clientWidth + 1)
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(scrollEl)
    const tableEl = scrollEl.querySelector('table')
    if (tableEl != null) {
      observer.observe(tableEl)
    }
    return () => observer.disconnect()
  }, [scrollRef, bindKey])

  useEffect(() => {
    const scrollEl = scrollRef.current
    const stickyEl = stickyRef.current
    if (!scrollEl || !stickyEl || !bindKey || !hasHorizontalOverflow) return

    const onTableScroll = () => {
      if (syncing.current) return
      syncing.current = true
      stickyEl.scrollLeft = scrollEl.scrollLeft
      requestAnimationFrame(() => {
        syncing.current = false
      })
    }

    const onStickyScroll = () => {
      if (syncing.current) return
      syncing.current = true
      scrollEl.scrollLeft = stickyEl.scrollLeft
      requestAnimationFrame(() => {
        syncing.current = false
      })
    }

    scrollEl.addEventListener('scroll', onTableScroll, { passive: true })
    stickyEl.addEventListener('scroll', onStickyScroll, { passive: true })
    return () => {
      scrollEl.removeEventListener('scroll', onTableScroll)
      stickyEl.removeEventListener('scroll', onStickyScroll)
    }
  }, [scrollRef, bindKey, hasHorizontalOverflow])

  return { stickyRef, scrollWidth, hasHorizontalOverflow }
}
